import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import CoordinatesSchema from "_shared/addresses/coordinates/coordinates-schema";
import { z } from "zod";

export type AddressJSON = z.infer<typeof AddressSchema.AddressJSONZ>;
export type AddressParams = z.infer<typeof AddressSchema.AddressZ>;

export default class AddressSchema {
  public static readonly AddressJSONZ = z.object({
    address1: z.string().nullish(),
    address2: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    postalCode: z.string().nullish(),
    country: z.string().nullish(),
    coordinates: CoordinatesSchema.CoordinatesJSONZ.nullish()
  });

  public static readonly AddressZ = z.object({
    address1: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
    country: z.string().optional(),
    coordinates: CoordinatesSchema.CoordinatesZ.optional()
  });

  public static validate(values: unknown): AddressParams {
    return parseOrThrow(
      AddressSchema.AddressZ,
      values,
      { 
        logError: true,
        schemaName: "AddressSchema",
        operation: "Validating AddressParams",
        payloadSample: values 
      }
    );
  }

  public static validateJSON(json: unknown): AddressJSON {
    return parseOrThrow(
      AddressSchema.AddressJSONZ,
      json,
      { 
        logError: true,
        schemaName: "AddressSchema",
        operation: "Validating AddressJSON",
        payloadSample: json 
      }
    );
  }

  public static isValid(values: unknown): values is AddressParams {
    try {
      AddressSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is AddressJSON {
    try {
      AddressSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: AddressJSON): AddressParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(
      AddressSchema.AddressJSONZ,
      json,
      { 
        logError: true,
        schemaName: "AddressSchema",
        operation: "deserialize AddressJSON",
        payloadSample: json
      }
    );
    return AddressSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: AddressParams): AddressJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = AddressSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return AddressSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    AddressSchema.AddressJSONZ,
    AddressSchema.AddressZ, {
    decode: (data) => ({
      address1: data.address1 ?? undefined,
      address2: data.address2 ?? undefined,
      city: data.city ?? undefined,
      state: data.state ?? undefined,
      postalCode: data.postalCode ?? undefined,
      country: data.country ?? undefined,
      coordinates: data.coordinates ? CoordinatesSchema.deserialize(data.coordinates) : undefined
    }),
    encode: (address) => ({
      address1: address.address1,
      address2: address.address2,
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      country: address.country,
      coordinates: address.coordinates ? CoordinatesSchema.serialize(address.coordinates) : undefined
    })
  });
}
